import { useState, useEffect } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

const fmtN = n => Number(n || 0).toLocaleString();

const LEVEL_DEFS = [
  { level: 1, tKey: 'lvlNew',    minXP: 0,    maxXP: 99,   color: 'var(--text3)',  bg: 'rgba(150,150,150,0.12)', border: 'rgba(150,150,150,0.3)' },
  { level: 2, tKey: 'lvlActive', minXP: 100,  maxXP: 499,  color: 'var(--cyan)',   bg: 'rgba(0,212,255,0.12)',   border: 'rgba(0,212,255,0.35)' },
  { level: 3, tKey: 'lvlPro',    minXP: 500,  maxXP: 1999, color: 'var(--purple)', bg: 'rgba(159,122,234,0.12)', border: 'rgba(159,122,234,0.35)' },
  { level: 4, tKey: 'lvlLegend', minXP: 2000, maxXP: Infinity, color: 'var(--yellow)', bg: 'rgba(255,215,0,0.12)', border: 'rgba(255,215,0,0.4)' },
];

const getLevelDef = xp => LEVEL_DEFS.slice().reverse().find(l => xp >= l.minXP) || LEVEL_DEFS[0];
const xpToNext = xp => {
  const next = LEVEL_DEFS.find(l => xp < l.maxXP);
  if (!next) return { needed: 0, pct: 100 };
  const range = next.maxXP - next.minXP + 1;
  const progress = xp - next.minXP;
  return { needed: next.maxXP - xp + 1, pct: Math.min(100, Math.round((progress / range) * 100)) };
};

const initials = name => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
};

function LevelBadge({ xp }) {
  const { t } = useLang();
  const lv = getLevelDef(xp);
  return (
    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 700,
      background: lv.bg, color: lv.color, border: `1px solid ${lv.border}`,
      boxShadow: lv.level === 4 ? `0 0 8px ${lv.color}50` : 'none' }}>
      Lv{lv.level} {t(lv.tKey)}
    </span>
  );
}

function Avatar({ name, size = 40 }) {
  const colors = ['var(--cyan)', 'var(--purple)', 'var(--green)', 'var(--orange)', 'var(--yellow)'];
  const color = colors[(name?.charCodeAt(0) || 0) % colors.length];
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: color + '25',
      border: `2px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, color, fontSize: size * 0.35, flexShrink: 0, fontFamily: 'Orbitron, sans-serif',
      position: 'relative' }}>
      {initials(name)}
    </div>
  );
}

export default function Leaderboard() {
  const { t } = useLang();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/leaderboard?limit=50').then(r => setPlayers(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const top3 = players.slice(0, 3);

  const totalXP  = players.reduce((s, p) => s + Number(p.xp || 0), 0);
  const avgXP    = players.length ? Math.round(totalXP / players.length) : 0;
  const topXP    = players[0]?.xp || 0;
  const totalHrs = players.reduce((s, p) => s + Number(p.total_hours || 0), 0);

  const podiumOrder = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;
  const podiumHeights = [130, 170, 110];
  const podiumCrowns  = ['🥈', '👑', '🥉'];
  const podiumColors  = ['rgba(192,192,192,0.3)', 'rgba(255,215,0,0.3)', 'rgba(205,127,50,0.3)'];
  const podiumBorders = ['rgba(192,192,192,0.5)', 'rgba(255,215,0,0.6)', 'rgba(205,127,50,0.5)'];

  if (loading) return (
    <div style={{ padding: 80, textAlign: 'center', color: 'var(--text3)' }}>{t('loading')}</div>
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>🏆 Leaderboard</h1>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>{t('leaderboardSubtitle')}</div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
        {[
          { icon: '👥', val: players.length, label: t('lbTotalPlayers'), color: 'var(--cyan)' },
          { icon: '⭐', val: fmtN(avgXP),    label: t('lbAvgXP'),        color: 'var(--purple)' },
          { icon: '🏆', val: fmtN(topXP),    label: t('lbTopXP'),        color: 'var(--yellow)' },
          { icon: '⏱️', val: `${Math.round(totalHrs)}h`, label: t('lbTotalHours'), color: 'var(--green)' },
        ].map(c => (
          <div key={c.label} style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14,
            padding: '18px 20px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60,
              borderRadius: '50%', background: c.color, filter: 'blur(24px)', opacity: 0.25 }} />
            <div style={{ fontSize: 22, marginBottom: 6 }}>{c.icon}</div>
            <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 20, fontWeight: 700, color: c.color }}>{c.val}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Podium */}
      {top3.length >= 3 && (
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 20,
          padding: '40px 40px 0', marginBottom: 28, overflow: 'hidden' }}>
          <div style={{ fontSize: 11, color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 30, textAlign: 'center' }}>
            {t('lbTop3Title')}
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 8 }}>
            {podiumOrder.map((p, i) => {
              if (!p) return null;
              const lv = getLevelDef(p.xp || 0);
              const isFirst = i === 1;
              return (
                <div key={p.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, maxWidth: 180 }}>
                  <div style={{ fontSize: isFirst ? 32 : 22, marginBottom: 8 }}>{podiumCrowns[i]}</div>
                  <Avatar name={p.name} size={isFirst ? 60 : 48} />
                  <div style={{ marginTop: 10, fontWeight: 700, fontSize: isFirst ? 15 : 13, textAlign: 'center' }}>
                    {p.name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{p.phone}</div>
                  <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: isFirst ? 18 : 14,
                    fontWeight: 700, color: lv.color, marginBottom: 8 }}>
                    {fmtN(p.xp)} XP
                  </div>
                  <LevelBadge xp={p.xp || 0} />
                  <div style={{ width: '100%', height: podiumHeights[i], marginTop: 16,
                    background: podiumColors[i], border: `2px solid ${podiumBorders[i]}`,
                    borderBottom: 'none', borderRadius: '10px 10px 0 0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 28, fontWeight: 900,
                      color: isFirst ? 'var(--yellow)' : 'var(--text3)', opacity: 0.6 }}>
                      {i === 1 ? '1' : i === 0 ? '2' : '3'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Full table */}
      <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 11,
          color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase' }}>{t('lbFullRating')}</div>
        {players.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>{t('noData')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--card)' }}>
                {['#', t('lbColPlayer'), t('lbColXPLevel'), t('lbColSessions'), t('lbColHours'), t('lbColProgress')].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {players.map((p, idx) => {
                const lv   = getLevelDef(p.xp || 0);
                const prog = xpToNext(p.xp || 0);
                const medalIcon = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : null;
                return (
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--border)',
                    background: idx < 3 ? lv.color.replace('var', '').replace('(', 'rgba(').replace(')', ', 0.03)') : 'transparent' }}>
                    <td style={{ padding: '12px 16px', fontFamily: 'Orbitron, sans-serif', fontWeight: 700,
                      color: idx < 3 ? lv.color : 'var(--text3)', fontSize: 14 }}>
                      {medalIcon || `#${idx + 1}`}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={p.name} size={32} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{p.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{p.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 14, fontWeight: 700,
                        color: lv.color, marginBottom: 4 }}>{fmtN(p.xp)} XP</div>
                      <LevelBadge xp={p.xp || 0} />
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--text)' }}>{p.total_sessions || 0}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--text2)' }}>
                      {Math.round(Number(p.total_hours || 0))}h
                    </td>
                    <td style={{ padding: '12px 16px', minWidth: 120 }}>
                      {prog.pct < 100 ? (
                        <>
                          <div style={{ height: 6, background: 'var(--border)', borderRadius: 4, overflow: 'hidden', marginBottom: 3 }}>
                            <div style={{ width: `${prog.pct}%`, height: '100%', background: lv.color, borderRadius: 4,
                              boxShadow: lv.level === 4 ? `0 0 6px ${lv.color}` : 'none' }} />
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text3)' }}>{prog.pct}% · {fmtN(prog.needed)} {t('xpNeeded')}</div>
                        </>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--yellow)', fontWeight: 700 }}>⭐ MAX</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
